import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { Sparkles, Send, Upload, FileText, Trash2, Loader2, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SESSION = "ayaan-main";
const SUGGESTIONS = [
  "Explain Newton's second law with an example",
  "Help me structure a persuasive essay",
  "What is the difference between mitosis and meiosis?",
  "Summarise the causes of World War 1",
];

export default function AITutor({ initialPrompt, onConsumePrompt }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [docs, setDocs] = useState([]);
  const [activeDoc, setActiveDoc] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const endRef = useRef(null);

  const loadDocs = () => api.get("/documents").then((r) => setDocs(r.data));
  useEffect(() => {
    api.get(`/chat/${SESSION}`).then((r) => setMessages(r.data));
    loadDocs();
  }, []);

  useEffect(() => {
    if (initialPrompt) {
      setInput(initialPrompt);
      onConsumePrompt && onConsumePrompt();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPrompt]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg, id: Date.now() }]);
    setSending(true);
    try {
      const r = await api.post("/chat", { session_id: SESSION, message: msg, doc_id: activeDoc });
      setMessages((m) => [...m, { role: "assistant", content: r.data.answer, id: Date.now() + 1 }]);
    } catch (e) {
      toast.error("AI is unavailable right now. Try again.");
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I couldn't answer that just now. Please try again.", id: Date.now() + 1 }]);
    } finally {
      setSending(false);
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("subject", "Common");
    try {
      const r = await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} uploaded`);
      setActiveDoc(r.data.id);
      loadDocs();
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeDoc = async (id) => {
    await api.delete(`/documents/${id}`);
    if (activeDoc === id) setActiveDoc(null);
    loadDocs();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      {/* Chat */}
      <div className="lg:col-span-2 flex flex-col bg-white rounded-2xl border border-slate-200 overflow-hidden" style={{ height: "72vh" }}>
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2 bg-gradient-to-r from-purple-50 to-indigo-50">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
            <Sparkles className="w-4.5 h-4.5 text-white" />
          </div>
          <div>
            <p className="font-heading font-semibold text-slate-900 leading-tight">Study Tutor · Claude</p>
            <p className="text-xs text-slate-500">{activeDoc ? "Answering from your uploaded document" : "Ask anything about your subjects"}</p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="chat-messages">
          {messages.length === 0 && !sending && (
            <div className="h-full flex flex-col items-center justify-center text-center px-6">
              <div className="w-14 h-14 rounded-2xl bg-purple-100 flex items-center justify-center mb-4">
                <Sparkles className="w-7 h-7 text-purple-600" />
              </div>
              <h3 className="font-heading font-semibold text-slate-900 text-lg">How can I help you study today?</h3>
              <p className="text-sm text-slate-500 mt-1 mb-5 max-w-sm">Ask a doubt, get concepts explained, or upload a chapter PDF and ask questions from it.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-lg">
                {SUGGESTIONS.map((s) => (
                  <button key={s} data-testid="suggestion-btn" onClick={() => send(s)} className="text-left text-sm px-4 py-3 rounded-xl border border-slate-200 hover:border-purple-300 hover:bg-purple-50 transition-colors text-slate-600">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m) => (
            <motion.div key={m.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-indigo-600 text-white rounded-br-md" : "bg-slate-100 text-slate-800 rounded-bl-md"}`}>
                {m.content}
              </div>
            </motion.div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-slate-100 rounded-2xl rounded-bl-md px-4 py-3 flex gap-1.5">
                {[0, 1, 2].map((i) => <span key={i} className="w-2 h-2 rounded-full bg-slate-400 thinking-dot" style={{ animationDelay: `${i * 0.2}s` }} />)}
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-4 border-t border-slate-100 flex gap-2">
          <Input data-testid="chat-input" placeholder="Ask your tutor..." value={input} onChange={(e) => setInput(e.target.value)} className="rounded-xl" />
          <Button data-testid="chat-send-btn" type="submit" disabled={sending} className="rounded-xl bg-indigo-600 hover:bg-indigo-700 px-4">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </form>
      </div>

      {/* Docs */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col">
        <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2 mb-1"><BookOpen className="w-4 h-4 text-purple-500" /> Books & Notes</h3>
        <p className="text-xs text-slate-500 mb-4">Upload a PDF and the tutor will answer from it.</p>

        <input ref={fileRef} type="file" accept=".pdf,.txt,.md" onChange={upload} className="hidden" data-testid="doc-file-input" />
        <Button data-testid="doc-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading} variant="outline" className="rounded-xl border-dashed border-2 h-20 mb-4 flex-col gap-1 hover:border-purple-400 hover:bg-purple-50">
          {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5 text-slate-400" />}
          <span className="text-xs text-slate-500">{uploading ? "Uploading..." : "Upload PDF / notes"}</span>
        </Button>

        <div className="space-y-2 overflow-y-auto flex-1" data-testid="doc-list">
          {docs.length === 0 && <p className="text-xs text-slate-400 text-center py-4">No documents yet.</p>}
          {docs.map((d) => (
            <div key={d.id} onClick={() => setActiveDoc(activeDoc === d.id ? null : d.id)} data-testid={`doc-item-${d.id}`}
              className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-colors ${activeDoc === d.id ? "border-purple-400 bg-purple-50" : "border-slate-200 hover:bg-slate-50"}`}>
              <FileText className={`w-4 h-4 shrink-0 ${activeDoc === d.id ? "text-purple-600" : "text-slate-400"}`} />
              <span className="text-xs text-slate-700 truncate flex-1">{d.original_filename}</span>
              <button onClick={(e) => { e.stopPropagation(); removeDoc(d.id); }} className="text-slate-300 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
        {activeDoc && <p className="text-xs text-purple-600 font-medium mt-3">✓ Answering from selected document</p>}
      </div>
    </div>
  );
}
